import React, { useState, useRef, useEffect, useMemo } from "react";

const MultiSelectDropdown = ({
  options = [],
  value = [],
  onSelect,
  placeholder = "Select...",
  buttonLabel,
  searchable = false,
  showCounts = false,
  className = "",
  align = "left",
  renderBadge,
  onScrollEnd,
  isLoadingMore = false,
  hasMore = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const optionsListRef = useRef(null);

  const selectedOptions = options.filter((opt) => value.includes(opt.value));

  const filteredOptions = useMemo(() => {
    if (!searchable || !searchQuery) return options;
    return options.filter((option) =>
      option.label.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [options, searchQuery, searchable]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setSearchQuery("");
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  const handleSelect = (optionValue) => {
    const newValues = value.includes(optionValue)
      ? value.filter((v) => v !== optionValue)
      : [...value, optionValue];
    onSelect(newValues);
  };

  const handleClearAll = (e) => {
    e.stopPropagation();
    onSelect([]);
    setSearchQuery("");
  };

  const handleScroll = (e) => {
    if (!onScrollEnd || !hasMore || isLoadingMore) return;
    
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop - clientHeight < 50) {
      onScrollEnd();
    }
  };

  const alignClasses = {
    left: "left-0",
    right: "right-0",
  };

  return (
    <div className={`relative ${className}`}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center gap-2 py-2.5 px-3 text-[13px] font-medium border border-line bg-white rounded-lg hover:bg-soft transition-all focus:outline-none focus:border-indigo focus:ring-3 focus:ring-indigo/10"
      >
        <span className="text-muted">+</span>
        <span className="text-ink">{buttonLabel || placeholder}</span>
        {selectedOptions.length > 0 && (
          <div className="flex items-center gap-1.5 ml-1">
            {selectedOptions.slice(0, 2).map((option) => (
              <span
                key={option.value}
                className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-soft text-ink rounded-md border border-line"
              >
                {renderBadge ? renderBadge(option) : option.label}
              </span>
            ))}
            {selectedOptions.length > 2 && (
              <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-soft text-muted rounded-md border border-line">
                +{selectedOptions.length - 2}
              </span>
            )}
          </div>
        )}
        <svg
          className={`w-4 h-4 text-muted transition-transform ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div
          ref={dropdownRef}
          className={`absolute z-50 mt-2 min-w-[200px] bg-white rounded-lg border border-line shadow-lg ${alignClasses[align]}`}
          style={{ width: triggerRef.current?.offsetWidth || "auto" }}
        >
          <div className="flex flex-col max-h-80">
            {searchable && (
              <div className="p-3 border-b border-line">
                <div className="relative">
                  <svg
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <circle cx="11" cy="11" r="7" />
                    <path d="m20 20-3.5-3.5" />
                  </svg>
                  <input
                    type="text"
                    placeholder={`Search ${buttonLabel?.toLowerCase() || ""}...`}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full py-2 pl-9 pr-3 text-sm border border-line rounded-lg focus:outline-none focus:border-indigo focus:ring-2 focus:ring-indigo/10"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              </div>
            )}

            <div 
              ref={optionsListRef}
              onScroll={handleScroll}
              className="overflow-auto max-h-60"
            >
              {filteredOptions.length === 0 ? (
                <div className="p-4 text-center text-sm text-muted">
                  No options found
                </div>
              ) : (
                <>
                  {filteredOptions.map((option) => {
                  const isSelected = value.includes(option.value);
                  return (
                    <div
                      key={option.value}
                      onClick={() => !option.disabled && handleSelect(option.value)}
                      className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-soft transition-colors ${
                        isSelected ? "bg-soft/50" : ""
                      } ${option.disabled ? "opacity-50 cursor-not-allowed" : ""}`}
                    >
                      <div
                        className={`w-4 h-4 border-2 rounded flex items-center justify-center transition-all ${
                          isSelected
                            ? "bg-[#0A1530] border-[#0A1530]"
                            : "border-line bg-white"
                        }`}
                      >
                        {isSelected && (
                          <svg
                            className="w-3 h-3 text-white"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={3}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        {option.icon && <div className="flex-shrink-0">{option.icon}</div>}
                        <span className="text-sm text-ink flex-1 truncate">{option.label}</span>
                        {showCounts && option.count > 0 && (
                          <span className="text-xs text-muted flex-shrink-0">
                            {option.count}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                  })}
                  {isLoadingMore && (
                    <div className="p-3 text-center text-sm text-muted">
                      Loading more...
                    </div>
                  )}
                </>
              )}
            </div>

            {selectedOptions.length > 0 && (
              <div className="p-3 border-t border-line">
                <button
                  onClick={handleClearAll}
                  className="w-full py-2 text-sm font-medium text-muted hover:text-ink hover:bg-soft rounded-lg transition-all"
                >
                  Clear filters
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
